import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faSearch } from '@fortawesome/free-solid-svg-icons';

import carBannerImg from '../../../assets/images/carbannerImg.jpg';
import { URLS } from '../../../constants/urls';
import { homeBannerPublicUrl } from '../../utils/homeBannerImage';
import { effectiveHomeWords, heroOverlayCss } from '../../utils/homePageSettings';
import { useHomePageSettings } from '../../hooks/useHomePageSettings';
import { BookCard } from '../../components/bookCard';
import { Modal } from '../../components/modal';
import { Colors, DesignTokens } from '../../styles/colors';
import { SCREENS, SCREENS_MAX } from '../../components/responsive';
import { getBrand } from '../../constants/brands';

// Background wrapper - full width with white background
const TopSectionWrapper = styled.div`
  ${tw`w-full max-w-full min-w-0 overflow-x-hidden box-border`};
  background: ${Colors.BgPrimary};
`;

// Container matching shared homepage content width.
const TopSectionOuter = styled.div`
  ${tw`
    w-full
    mx-auto
    px-2
    md:px-4
    lg:px-0
    pt-2
    md:pt-4
    box-border
  `};
  max-width: ${DesignTokens.container.maxWidth};
`;

const TopSectionContainer = styled.div<{ $image?: string; $overlay: string }>`
  ${tw`
    w-full
    relative
    flex
    flex-col
    items-center
    box-border
    overflow-hidden
  `};
  min-height: auto;
  border-radius: 16px;

  @media (min-width: ${SCREENS.md}) {
    min-height: auto;
    border-radius: 24px;
  }
  background-color: #18181b;
  background-image: ${({ $image }) => ($image ? `url(${JSON.stringify($image)})` : 'none')};
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 16px;
    /* Spec 029: the brand's overlay, else today's (STANDARD_OVERLAY_CSS). */
    background: ${({ $overlay }) => $overlay};

    @media (min-width: ${SCREENS.md}) {
      border-radius: 24px;
    }
  }

  @media (min-width: ${SCREENS.lg}) {
    min-height: auto;
  }
`;

const ContentContainer = styled.div`
  ${tw`
    w-full
    flex
    flex-col
    items-center
    text-center
    gap-6
    z-10 max-w-full min-w-0 box-border`};
  padding: 24px 16px 28px;

  @media (min-width: ${SCREENS.md}) {
    padding: 56px 40px 48px;
    gap: 8px;
  }

  @media (min-width: ${SCREENS.lg}) {
    padding: 64px 56px 56px;
  }
`;

const HeroTextBlock = styled.div`
  ${tw`
    w-full
    flex
    flex-col
    items-center
    max-w-2xl
  `};
`;

const Slogan = styled.h1`
  ${tw`
    font-display
    font-bold
    mb-3
    md:mb-4
  `};
  font-size: ${DesignTokens.typography.fontSize.display1};
  line-height: ${DesignTokens.typography.lineHeight.display1};
  color: ${Colors.White};

  @media (min-width: ${SCREENS.md}) {
    font-size: ${DesignTokens.typography.fontSize.display1Md};
  }
`;

const Description = styled.p`
  ${tw`
    max-w-md
    mb-2
    md:mb-6
  `};
  font-size: ${DesignTokens.typography.fontSize.bodySm};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: rgba(255, 255, 255, 0.85);

  @media (min-width: ${SCREENS.md}) {
    font-size: ${DesignTokens.typography.fontSize.body};
  }
`;

const SearchCardWrapper = styled.div`
  ${tw`
    w-full
    mx-auto
  `};

  @media (max-width: ${SCREENS.md}) {
    max-width: 400px;
  }

  @media (max-width: ${SCREENS_MAX.sm}) {
    max-width: 90%;
  }

  @media (min-width: ${SCREENS.lg}) {
    max-width: 900px;
  }
`;

const searchTriggerBase = css`
  width: 100%;
  background: white;
  border: 1px solid ${Colors.BorderLight};
  cursor: pointer;
  transition: all 0.2s ease-out;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
  }

  &:focus-visible {
    outline: 3px solid rgba(10, 10, 10, 0.2);
    outline-offset: 3px;
  }
`;

const searchBarRadius = '12px';

const SearchTriggerPlaceholder = styled.span`
  ${tw`
    flex-1
    text-left
    text-base
    font-medium
    truncate
    pl-1
  `};
  color: ${Colors.Gray500};
`;

const SearchTriggerAction = styled.span`
  ${tw`
    w-10
    h-10
    rounded-lg
    flex
    items-center
    justify-center
    flex-shrink-0
    transition-colors
    duration-200
  `};
  background: ${Colors.Black};
  color: ${Colors.White};
`;

const DesktopSearchAction = styled(SearchTriggerAction)`
  ${tw`m-[5px] self-center`}
  width: 44px;
  height: 44px;
`;

const searchTriggerHoverAction = css`
  &:hover ${SearchTriggerAction} {
    background: ${Colors.BlackHover};
  }
`;

/** Mobile/tablet (< lg): compact bar — "Search anywhere" + black search button */
const SearchTriggerMobile = styled.button`
  ${tw`
    flex
    lg:hidden
    items-center
    justify-between
    gap-3
    px-4
    py-2
  `};
  ${searchTriggerBase}
  ${searchTriggerHoverAction}
  border-radius: ${searchBarRadius};
  min-height: 56px;
`;

/** Desktop (>= lg): segmented Where / From / Until + black search button */
const SearchTriggerDesktop = styled.button`
  ${tw`
    hidden
    lg:flex
    items-stretch
    w-full
    text-left
    overflow-hidden
  `};
  ${searchTriggerBase}
  ${searchTriggerHoverAction}
  border-radius: ${searchBarRadius};
  width: 900px;
  max-width: 100%;
  min-height: 54px;
  height: 54px;
  padding: 0;
`;

const DesktopSegment = styled.span<{ $wide?: boolean }>`
  ${tw`
    flex
    flex-col
    justify-center
    flex-1
    min-w-0
    px-4
    py-2
  `};
  ${props => props.$wide && tw`flex-[1.08]`}
  border-right: 1px solid ${Colors.BorderLight};
`;

const DesktopSegmentLabel = styled.span`
  ${tw`
    font-semibold
    mb-0.5
  `};
  font-size: ${DesignTokens.typography.fontSize.label};
  line-height: ${DesignTokens.typography.lineHeight.label};
  color: ${Colors.Gray700};
`;

const DesktopSegmentValue = styled.span`
  ${tw`
    truncate
  `};
  font-size: ${DesignTokens.typography.fontSize.bodySm};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: ${Colors.Gray500};
`;

const DateTimeRow = styled.span`
  ${tw`
    flex
    flex-row
    items-start
    gap-5
    min-w-0
    items-center
  `};
`;

const DateTimeChip = styled.span`
  ${tw`
    flex
    items-center
    gap-1
    whitespace-nowrap
  `};
  font-size: ${DesignTokens.typography.fontSize.bodySm};
  line-height: ${DesignTokens.typography.lineHeight.body};
  color: ${Colors.Gray500};
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  ${tw`opacity-70`}
  font-size: 10px;
`;

const SearchModalContent = styled.div`
  ${tw`
    w-full
    pt-8
    md:pt-0
  `};
`;

function openSearch(setSearchModalOpen: (open: boolean) => void) {
  return () => setSearchModalOpen(true);
}

export function TopSection() {
  const brand = getBrand();
  // Spec 029: cached first, then the API (banner, overlay and words).
  const { bannerKey, bannerResolved, homePage, ready } = useHomePageSettings(brand.brandSite);
  const customBanner = homeBannerPublicUrl(bannerKey, URLS.S3_BUCKET.CAR_THUMBNAILS);
  const bannerImage = customBanner || (bannerResolved ? carBannerImg : undefined);
  const words = effectiveHomeWords(brand, homePage);
  const overlay = heroOverlayCss(homePage?.overlayColor ?? null, homePage?.overlayOpacity ?? null);
  const [isSearchModalOpen, setSearchModalOpen] = useState(false);

  return (
    <TopSectionWrapper>
      <TopSectionOuter>
        <TopSectionContainer $image={bannerImage} $overlay={overlay}>
          <ContentContainer>
            {/* Spec 029 D6: with nothing cached the words wait (at most 1.5 s) so a
                brand's own words do not replace the defaults after a flash. */}
            <HeroTextBlock style={ready ? undefined : { visibility: 'hidden' }} aria-busy={!ready}>
              <Slogan data-testid="home-hero-title">{words.webTitle}</Slogan>
              <Description data-testid="home-hero-subtitle">{words.webSubtitle}</Description>
            </HeroTextBlock>
            <SearchCardWrapper>
              <SearchTriggerMobile
                type="button"
                onClick={openSearch(setSearchModalOpen)}
                aria-label="Open search"
              >
                <SearchTriggerPlaceholder>Search anywhere</SearchTriggerPlaceholder>
                <SearchTriggerAction aria-hidden>
                  <FontAwesomeIcon icon={faSearch} />
                </SearchTriggerAction>
              </SearchTriggerMobile>

              <SearchTriggerDesktop
                type="button"
                onClick={openSearch(setSearchModalOpen)}
                aria-label="Open search options"
              >
                <DesktopSegment $wide>
                  <DesktopSegmentLabel>Where</DesktopSegmentLabel>
                  <DesktopSegmentValue>Airport, hotel, address, city</DesktopSegmentValue>
                </DesktopSegment>
                <DesktopSegment>
                  <DesktopSegmentLabel>From</DesktopSegmentLabel>
                  <DateTimeRow>
                    <DateTimeChip>
                      Add dates <ChevronIcon icon={faChevronDown} />
                    </DateTimeChip>
                    <DateTimeChip>
                      Add time <ChevronIcon icon={faChevronDown} />
                    </DateTimeChip>
                  </DateTimeRow>
                </DesktopSegment>
                <DesktopSegment>
                  <DesktopSegmentLabel>Until</DesktopSegmentLabel>
                  <DateTimeRow>
                    <DateTimeChip>
                      Add dates <ChevronIcon icon={faChevronDown} />
                    </DateTimeChip>
                    <DateTimeChip>
                      Add time <ChevronIcon icon={faChevronDown} />
                    </DateTimeChip>
                  </DateTimeRow>
                </DesktopSegment>
                <DesktopSearchAction aria-hidden>
                  <FontAwesomeIcon icon={faSearch} />
                </DesktopSearchAction>
              </SearchTriggerDesktop>
            </SearchCardWrapper>
          </ContentContainer>
        </TopSectionContainer>
      </TopSectionOuter>
      <Modal
        isOpen={isSearchModalOpen}
        onClose={() => setSearchModalOpen(false)}
        modalWidth="min(560px, calc(100vw - 24px))"
      >
        <SearchModalContent>
          <BookCard absolute={false} onSearchComplete={() => setSearchModalOpen(false)} />
        </SearchModalContent>
      </Modal>
    </TopSectionWrapper>
  );
}

export default TopSection;
